/**
 * Client detail — tabs: Informações + Contratos + Chamados.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2 } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { DetailCard, DetailLayout, KeyValue } from "@/components/detail-layout";
import { EntityActions } from "@/components/entity-actions";
import { JobStatusPill } from "@/components/status-pill";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { clientsApi, contractsApi, type Client } from "@/lib/api-cadastros";
import { jobsApi } from "@/lib/api-jobs";
import { formatCNPJ, formatCurrency, formatDate, formatPhone } from "@/lib/format";

export function ClientDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<Partial<Client>>({});

  const client = useQuery({ queryKey: ["client", id], queryFn: () => clientsApi.get(id), enabled: !!id });
  const contracts = useQuery({
    queryKey: ["client-contracts", id],
    queryFn: () => contractsApi.list({ client_id: id, size: 200 }),
    enabled: !!id,
  });
  const jobs = useQuery({
    queryKey: ["client-jobs", id],
    queryFn: () => jobsApi.list({ client_id: id, size: 50 }),
    enabled: !!id,
  });

  const update = useMutation({
    mutationFn: (body: Partial<Client>) => clientsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client", id] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente atualizado");
      setEditOpen(false);
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const openEdit = () => {
    if (client.data) {
      setForm({
        trade_name: client.data.trade_name,
        corporate_name: client.data.corporate_name,
        cnpj: client.data.cnpj,
        email: client.data.email,
        phone: client.data.phone,
        website: client.data.website,
        notes: client.data.notes,
        active: client.data.active,
      });
      setEditOpen(true);
    }
  };

  if (!client.data) {
    return <div className="text-muted-foreground py-10">Carregando…</div>;
  }
  const c = client.data;
  return (
    <DetailLayout
      backTo="/clients"
      eyebrow="Cliente"
      visual={
        <Avatar size="xl">
          <AvatarFallback name={c.trade_name} />
        </Avatar>
      }
      title={c.trade_name}
      subtitle={c.corporate_name}
      badges={
        <>
          {c.active ? <Badge variant="success" dot>Ativo</Badge> : <Badge variant="muted" dot>Inativo</Badge>}
          <Badge variant="default" className="font-mono">{formatCNPJ(c.cnpj)}</Badge>
        </>
      }
      actions={
        <EntityActions
          onEdit={openEdit}
          onDelete={() => clientsApi.remove(id)}
          redirectAfterDelete="/clients"
          invalidateQueries={["clients"]}
          resourceLabel="cliente"
        />
      }
    >
      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Informações</TabsTrigger>
          <TabsTrigger value="contracts">Contratos ({contracts.data?.total ?? 0})</TabsTrigger>
          <TabsTrigger value="jobs">Chamados ({jobs.data?.total ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailCard title="Identificação">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <KeyValue label="Razão social" value={c.corporate_name} />
                <KeyValue label="Nome fantasia" value={c.trade_name} />
                <KeyValue label="CNPJ" value={<span className="font-mono">{formatCNPJ(c.cnpj)}</span>} />
                <KeyValue label="Status" value={c.active ? "Ativo" : "Inativo"} />
              </div>
            </DetailCard>
            <DetailCard title="Contato">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <KeyValue label="Email" value={c.email} />
                <KeyValue label="Telefone" value={formatPhone(c.phone)} />
                <KeyValue label="Website" value={c.website ?? "—"} />
              </div>
            </DetailCard>
            {c.address && (
              <DetailCard title="Endereço" className="md:col-span-2">
                <KeyValue
                  label="Endereço completo"
                  value={`${c.address.street}, ${c.address.number ?? "s/n"} ${
                    c.address.complement ? `— ${c.address.complement}` : ""
                  } · ${c.address.district ?? ""} · ${c.address.cidade?.name ?? ""}/${c.address.cidade?.uf ?? ""}`}
                />
              </DetailCard>
            )}
            {c.notes && (
              <DetailCard title="Observações" className="md:col-span-2">
                <p className="text-sm whitespace-pre-wrap">{c.notes}</p>
              </DetailCard>
            )}
          </div>
        </TabsContent>

        <TabsContent value="contracts">
          {contracts.data && contracts.data.items.length > 0 ? (
            <DetailCard>
              <div className="divide-y divide-slate-200/40 dark:divide-white/5 -m-6">
                {contracts.data.items.map((ct) => (
                  <Link
                    key={ct.id}
                    to={`/contracts/${ct.id}`}
                    className="flex items-center justify-between gap-4 px-6 py-3.5 hover:bg-slate-50/60 dark:hover:bg-white/[0.03] transition-colors"
                  >
                    <div>
                      <p className="font-medium">{ct.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(ct.starts_at)} — {ct.ends_at ? formatDate(ct.ends_at) : "indefinido"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant={ct.active ? "success" : "muted"}>{ct.type}</Badge>
                      {ct.monthly_credit_limit && (
                        <span className="text-sm font-semibold tabular-nums">{formatCurrency(ct.monthly_credit_limit)}/mês</span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </DetailCard>
          ) : (
            <EmptyState
              icon={Building2}
              title="Sem contratos"
              description="Este cliente ainda não tem contratos cadastrados."
            />
          )}
        </TabsContent>

        <TabsContent value="jobs">
          {jobs.data && jobs.data.items.length > 0 ? (
            <DetailCard>
              <div className="divide-y divide-slate-200/40 dark:divide-white/5 -m-6">
                {jobs.data.items.map((j) => (
                  <Link
                    key={j.id}
                    to={`/jobs/${j.id}`}
                    className="flex items-center justify-between gap-4 px-6 py-3.5 hover:bg-slate-50/60 dark:hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{j.code}</span>
                      </div>
                      <p className="font-medium truncate">{j.title}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold tabular-nums">{formatCurrency(j.price)}</span>
                      <JobStatusPill status={j.status} />
                    </div>
                  </Link>
                ))}
              </div>
            </DetailCard>
          ) : (
            <EmptyState title="Sem chamados deste cliente" />
          )}
        </TabsContent>
      </Tabs>

      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent size="lg">
          <SheetHeader>
            <SheetTitle>Editar cliente</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <Field label="Nome fantasia" required>
              <Input value={form.trade_name ?? ""} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} />
            </Field>
            <Field label="Razão social" required>
              <Input value={form.corporate_name ?? ""} onChange={(e) => setForm({ ...form, corporate_name: e.target.value })} />
            </Field>
            <Field label="CNPJ (14 dígitos)" required>
              <Input
                value={form.cnpj ?? ""}
                onChange={(e) => setForm({ ...form, cnpj: e.target.value.replace(/\D/g, "") })}
                maxLength={14}
                className="font-mono"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <Input type="email" value={form.email ?? ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="Telefone">
                <Input value={form.phone ?? ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
            </div>
            <Field label="Website">
              <Input value={form.website ?? ""} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </Field>
            <Field label="Observações">
              <Textarea rows={3} value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild><Button variant="outline">Cancelar</Button></SheetClose>
            <Button onClick={() => update.mutate(form)} disabled={update.isPending}>
              {update.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </DetailLayout>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label} {required && <span className="text-rose-600">*</span>}
      </Label>
      {children}
    </div>
  );
}
